import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, CheckCircle2, Send, Cloud, RefreshCw, AlertTriangle, UploadCloud, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import Swal from 'sweetalert2';
import { initDB } from '../db/db';

const compressImage = (file, maxWidth = 800, quality = 0.7) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    img.src = URL.createObjectURL(file);
  });
};

const ObFormModal = ({ isOpen, onClose, task, onImageSelected, serverReports = [], apiQueue }) => {
  const [images, setImages] = useState({});
  const [subTaskStates, setSubTaskStates] = useState({});
  const [submittedTasks, setSubmittedTasks] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // remedialMode: subtaskId yang sedang dalam mode kerjakan ulang (ada catatan dari admin)
  const [remedialMode, setRemedialMode] = useState({});
  const [remedialImages, setRemedialImages] = useState({});

  // Temporary file states for uploading real Blobs/Files
  const [tempFiles, setTempFiles] = useState({});
  const [tempRemedialFiles, setTempRemedialFiles] = useState({});
  const [newExtraTaskFile, setNewExtraTaskFile] = useState(null);

  // States untuk penambahan tugas tambahan secara mandiri oleh OB
  const [newExtraTaskName, setNewExtraTaskName] = useState('');
  const [newExtraTaskImage, setNewExtraTaskImage] = useState(null);
  const [isCreatingExtra, setIsCreatingExtra] = useState(false);

  // States untuk Upload Log panel
  const [showUploadLog, setShowUploadLog] = useState(false);
  const [uploadLogData, setUploadLogData] = useState([]);
  const [pushingIds, setPushingIds] = useState({});

  // Helper: bangun data upload log dari localStorage + serverReports
  const buildUploadLog = useCallback(() => {
    if (!task) return [];
    const storageKey = `ob_progress_${task.id}`;
    const savedProgress = JSON.parse(localStorage.getItem(storageKey) || '{}');

    return task.subTasks.map((subTask) => {
      const subTaskId = subTask.id;
      const local = savedProgress[subTaskId] || null;
      const serverMatch = serverReports.find(
        r => parseInt(r.task_id) === parseInt(task.id) && parseInt(r.subtask_index) === parseInt(subTaskId)
      );
      return {
        subTaskId,
        title: subTask.title,
        localSaved: !!local,
        localTime: local?.time || null,
        localImage: local?.image || null,
        serverStatus: serverMatch?.status || null,
        serverAt: serverMatch?.created_at || serverMatch?.updated_at || null,
      };
    });
  }, [task, serverReports]);

  useEffect(() => {
    if (task) {
      const storageKey = `ob_progress_${task.id}`;
      const savedProgress = JSON.parse(localStorage.getItem(storageKey) || '{}');
      
      const initialState = {};
      const initialImages = {};
      const initialSubmitted = {};

      task.subTasks.forEach((subTask) => {
        const subTaskId = subTask.id;
        const saved = savedProgress[subTaskId] || {};

        // Cek apakah subtask ini sudah ada di server (submitted dari sesi sebelumnya)
        const serverMatch = serverReports.find(
          r => parseInt(r.task_id) === parseInt(task.id) && parseInt(r.subtask_index) === parseInt(subTaskId)
        );
        const isSubmittedByServer = !!serverMatch;

        // Gabungkan: submitted jika ada di local ATAU ada di server
        const isSubmitted = saved.submitted || isSubmittedByServer;

        initialState[subTaskId] = isSubmitted;
        initialSubmitted[subTaskId] = isSubmitted;

        // Prioritas gambar: local base64 > server photo_path URL
        if (saved.image) {
          // Gambar tersimpan lokal (base64) — gunakan langsung
          initialImages[subTaskId] = saved.image;
        } else if (isSubmittedByServer && serverMatch.photo_path) {
          // Tidak ada gambar lokal, tapi server punya foto — bangun URL lengkapnya
          const baseUrl = 'https://jne-cilacap.com/slip/images';
          const photoPath = serverMatch.photo_path;
          initialImages[subTaskId] = photoPath.startsWith('http')
            ? photoPath
            : `${baseUrl}${photoPath.startsWith('/') ? '' : '/'}${photoPath}`;
        } else {
          initialImages[subTaskId] = null;
        }

        // Jika sudah dikonfirmasi server tapi local belum tercatat, update localStorage
        if (isSubmittedByServer && !saved.submitted) {
          savedProgress[subTaskId] = {
            ...saved,
            submitted: true,
            time: saved.time || serverMatch.created_at || new Date().toISOString()
          };
        }
      });

      // Simpan update lokal jika ada perubahan dari server
      localStorage.setItem(storageKey, JSON.stringify(savedProgress));

      setSubTaskStates(initialState);
      setImages(initialImages);
      setSubmittedTasks(initialSubmitted);
      setRemedialMode({});
      setRemedialImages({});
      setTempFiles({});
      setTempRemedialFiles({});
      setNewExtraTaskFile(null);
      setShowUploadLog(false);
      setPushingIds({});

      // Reset form tugas tambahan setiap kali modal dibuka atau task berganti
      setNewExtraTaskName('');
      setNewExtraTaskImage(null);

      // Async loading of local images from IndexedDB
      const loadLocalImages = async () => {
        try {
          const db = await initDB();
          const loadedImages = {};
          for (const subTask of task.subTasks) {
            const subTaskId = subTask.id;
            const saved = savedProgress[subTaskId] || {};
            // Jika ada status disubmit tapi tidak ada gambar local (base64) maupun server
            if (!initialImages[subTaskId] && saved.submitted) {
              const record = await db.get('fotos', `ob_${task.id}_${subTaskId}`);
              if (record && record.blob) {
                loadedImages[subTaskId] = URL.createObjectURL(record.blob);
              }
            }
          }
          if (Object.keys(loadedImages).length > 0) {
            setImages(prev => ({
              ...prev,
              ...loadedImages
            }));
          }
        } catch (err) {
          console.error('Error loading local images from IndexedDB:', err);
        }
      };

      loadLocalImages();
    }
  }, [task, isOpen, serverReports]);

  // Refresh log setiap kali showUploadLog dibuka atau serverReports berubah
  useEffect(() => {
    if (showUploadLog) {
      setUploadLogData(buildUploadLog());
    }
  }, [showUploadLog, buildUploadLog, serverReports]);

  // Handler: push ulang satu subtask ke server
  const handlePushSubTask = async (logItem) => {
    const { subTaskId, title } = logItem;
    setPushingIds(prev => ({ ...prev, [subTaskId]: true }));
    const userData = JSON.parse(localStorage.getItem('data_karyawan') || '{}');

    const doUpload = async () => {
      try {
        const db = await initDB();
        const record = await db.get('fotos', `ob_${task.id}_${subTaskId}`);
        const blob = record?.blob;
        
        if (!blob) {
          throw new Error('Foto lokal tidak ditemukan. Silakan ambil foto ulang.');
        }

        const formData = new FormData();
        formData.append('photo', blob, `ob_${task.id}_${subTaskId}.jpg`);
        formData.append('task_id', task.id);
        formData.append('subtask_index', subTaskId);
        formData.append('user_id', userData.id);

        const res = await fetch('https://jne-cilacap.com/slip/public/api/ob/report', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData,
        });
        const json = await res.json();
        if (json.status === 'success' || res.ok) {
          Swal.fire({ icon: 'success', title: 'Berhasil!', text: `"${title}" berhasil dikirim ke server.`, timer: 2000, showConfirmButton: false, toast: true, position: 'top-end' });
          window.dispatchEvent(new Event('ob-tasks-updated'));
          // Refresh log
          setUploadLogData(buildUploadLog());
        } else {
          throw new Error(json.message || 'Gagal upload');
        }
      } catch (err) {
        Swal.fire('Gagal', err.message || 'Terjadi kesalahan saat upload.', 'error');
      } finally {
        setPushingIds(prev => ({ ...prev, [subTaskId]: false }));
      }
    };

    if (apiQueue) {
      apiQueue.add(doUpload).finally(() => setPushingIds(prev => ({ ...prev, [subTaskId]: false })));
    } else {
      doUpload();
    }
  };

  const handleCreateAndSubmitExtraTask = () => {
    if (!newExtraTaskName.trim()) {
      Swal.fire('Peringatan', 'Nama tugas tambahan harus diisi.', 'warning');
      return;
    }
    if (!newExtraTaskImage) {
      Swal.fire('Peringatan', 'Foto bukti pengerjaan harus diambil.', 'warning');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const taskName = newExtraTaskName.trim();
    
    // Validasi: Cek apakah tugas tambahan dengan nama serupa sudah ada di server
    const duplicateExtraTask = serverReports.find(r =>
      parseInt(r.task_id) === parseInt(task.id) &&
      r.item_name?.toLowerCase().includes(taskName.toLowerCase())
    );
    
    if (duplicateExtraTask) {
      Swal.fire({
        icon: 'warning',
        title: 'Tugas Duplikasi',
        text: `Tugas "${duplicateExtraTask.item_name}" sudah pernah dilaporkan. Lanjutkan membuat tugas baru?`,
        showCancelButton: true,
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Lanjutkan',
        cancelButtonText: 'Batal'
      }).then((result) => {
        if (result.isConfirmed) {
          // Proses pembuatan tugas
          proceedCreateExtraTask(taskName);
        }
      });
      return;
    }
    
    proceedCreateExtraTask(taskName);
  };

  const proceedCreateExtraTask = async (taskName) => {
    const userData = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const tempId = 'temp_' + Date.now();
    const taskFile = newExtraTaskFile;

    if (!taskFile) {
      Swal.fire('Peringatan', 'Foto tidak ditemukan.', 'warning');
      return;
    }

    try {
      // 1. Compress and save to IndexedDB under tempId
      const db = await initDB();
      const compressedBlob = await compressImage(taskFile);
      const tempDbKey = `ob_${task.id}_${tempId}`;
      await db.put('fotos', { timestamp: tempDbKey, blob: compressedBlob });

      // 2. Save preview to images state so it shows up instantly
      const objectUrl = URL.createObjectURL(taskFile);
      setImages(prev => ({
        ...prev,
        [tempId]: objectUrl
      }));

      // 3. Simpan tugas tambahan lokal agar langsung tampil di UI
      const localExtraKey = `ob_local_extra_tasks_${task.id}`;
      const localExtras = JSON.parse(localStorage.getItem(localExtraKey) || '[]');

      // Tentukan nama penambah berdasarkan jabatan karyawan
      const jabatan = (userData.jabatan || '').toLowerCase();
      let added_by_name = null;
      if (jabatan.includes('general affair')) added_by_name = 'Retno Adi Nugroho';
      else if (jabatan.includes('human capital')) added_by_name = 'Kamalia Nur Fajriyah';

      const newTask = {
        id: tempId,
        task_id: task.id,
        item_name: taskName,
        user_id: userData.id,
        added_by_name,
        is_local_pending: true
      };
      localExtras.push(newTask);
      localStorage.setItem(localExtraKey, JSON.stringify(localExtras));

      // 4. Simpan progress lokal agar langsung centang terupload (hijau) — OMIT base64!
      const progressKey = `ob_progress_${task.id}`;
      const progress = JSON.parse(localStorage.getItem(progressKey) || '{}');
      progress[tempId] = {
        submitted: true,
        time: new Date().toISOString()
      };
      localStorage.setItem(progressKey, JSON.stringify(progress));

      // 5. Pemicu callback optimistik di parent (Lapor.js)
      if (onImageSelected) {
        onImageSelected(task.id, tempId);
      }

      // 6. Reset input modal & tutup modal
      setNewExtraTaskName('');
      setNewExtraTaskImage(null);
      setNewExtraTaskFile(null);

      Swal.fire({
        icon: 'success',
        title: 'Tugas Tambahan Dibuat',
        text: 'Mengunggah di latar belakang...',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });

      onClose();

      // 7. Jalankan pengiriman di background
      const runBackgroundUpload = async () => {
        try {
          console.log('[OB Background] Starting task creation...', taskName);
          
          // Step 1: Buat tugas tambahan di server
          const createRes = await fetch('https://jne-cilacap.com/slip/public/api/ob/extra-task', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              task_id: task.id,
              item_name: taskName,
              user_id: userData.id
            })
          });

          const createJson = await createRes.json();
          console.log('[OB Background] Extra task created:', createJson);

          if (createJson.status !== 'success') {
            throw new Error(createJson.message || 'Gagal membuat tugas di server');
          }

          const newServerId = createJson.data?.id || createJson.item?.id || createJson.id;
          if (!newServerId) {
            throw new Error('ID tugas baru tidak ditemukan dari response server');
          }

          // Step 2: Ambil foto dari IndexedDB dan Upload ke server
          const dbInstance = await initDB();
          const record = await dbInstance.get('fotos', tempDbKey);
          const blob = record?.blob;
          if (!blob) throw new Error('Foto bukti tidak ditemukan di IndexedDB');

          const formData = new FormData();
          formData.append('photo', blob, `ob_${task.id}_${newServerId}.jpg`);
          formData.append('task_id', task.id);
          formData.append('subtask_index', newServerId);
          formData.append('user_id', userData.id);

          const reportResponse = await fetch('https://jne-cilacap.com/slip/public/api/ob/report', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: formData,
          });

          const reportJson = await reportResponse.json();
          console.log('[OB Background] Report upload success:', reportJson);

          // Step 3: Simpan foto di IndexedDB menggunakan newServerId
          await dbInstance.put('fotos', { timestamp: `ob_${task.id}_${newServerId}`, blob });
          // Bersihkan tempId lama di IndexedDB
          await dbInstance.delete('fotos', tempDbKey);

          // Step 4: Pindahkan progress dari tempId ke newServerId di localStorage
          const currentProgress = JSON.parse(localStorage.getItem(progressKey) || '{}');
          currentProgress[newServerId] = {
            submitted: true,
            time: new Date().toISOString()
          };
          delete currentProgress[tempId];
          localStorage.setItem(progressKey, JSON.stringify(currentProgress));

          // Step 5: Bersihkan dari tugas tambahan lokal pending
          const currentLocalExtras = JSON.parse(localStorage.getItem(localExtraKey) || '[]');
          const updatedExtras = currentLocalExtras.filter(e => e.id !== tempId);
          localStorage.setItem(localExtraKey, JSON.stringify(updatedExtras));

          console.log('[OB Background] Background upload fully completed.');
        } catch (error) {
          console.error('[OB Background] Sync error:', error);
        } finally {
          // Dispatch window event agar Lapor.js me-refresh data
          window.dispatchEvent(new Event('ob-tasks-updated'));
        }
      };

      // Gunakan queue jika tersedia untuk sequential uploads
      if (apiQueue) {
        apiQueue.add(runBackgroundUpload);
      } else {
        runBackgroundUpload();
      }
    } catch (err) {
      console.error('Gagal membuat tugas tambahan:', err);
      Swal.fire('Gagal', err.message || 'Terjadi kesalahan.', 'error');
    }
  };

  if (!isOpen || !task) return null;

  const handleToggleSubTask = (subTaskId) => {
    setSubTaskStates(prev => ({
      ...prev,
      [subTaskId]: !prev[subTaskId]
    }));
  };

  const handleFileSelect = (subTaskId, file, isRemedial = false) => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      if (isRemedial) {
        setRemedialImages(prev => ({
          ...prev,
          [subTaskId]: objectUrl
        }));
        setTempRemedialFiles(prev => ({
          ...prev,
          [subTaskId]: file
        }));
      } else {
        setImages(prev => ({
          ...prev,
          [subTaskId]: objectUrl
        }));
        setTempFiles(prev => ({
          ...prev,
          [subTaskId]: file
        }));
        setSubTaskStates(prev => ({
          ...prev,
          [subTaskId]: true
        }));
      }
    }
  };

  const handleImageChange = (subTaskId, e) => {
    const file = e.target.files[0];
    handleFileSelect(subTaskId, file, false);
  };

  const dataURLtoBlob = (dataurl) => {
    try {
      var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch (e) {
      console.error('[OB Debug] dataURLtoBlob error:', e);
      return null;
    }
  };

  const handleSubmitSubTask = async (subTask, isRemedial = false) => {
    const subTaskId = subTask.id;
    const targetFile = isRemedial ? tempRemedialFiles[subTaskId] : tempFiles[subTaskId];
    
    if (!targetFile) {
      Swal.fire('Peringatan', 'Foto tidak ditemukan.', 'warning');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    
    // Validasi: Cek apakah task ini sudah pernah disubmit ke server
    const existingReport = serverReports.find(r =>
      parseInt(r.task_id) === parseInt(task.id) &&
      parseInt(r.subtask_index) === parseInt(subTaskId)
    );
    
    if (existingReport && !isRemedial) {
      // Jika sudah ada di server dan bukan mode remidi, tampilkan warning
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Tugas Sudah Pernah Dilaporkan',
        text: `Tugas ini sudah pernah disubmit (Status: ${existingReport.status || 'Terkirim'}). Laporkan ulang?`,
        showCancelButton: true,
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Ya, Laporkan Ulang',
        cancelButtonText: 'Batal'
      });
      if (!result.isConfirmed) return;
    }

    try {
      // 1. Compress and Save to IndexedDB
      const db = await initDB();
      const compressedBlob = await compressImage(targetFile);
      const dbKey = `ob_${task.id}_${subTaskId}`;
      await db.put('fotos', { timestamp: dbKey, blob: compressedBlob });

      // 2. Update status & image states locally for instant feedback
      const objectUrl = URL.createObjectURL(targetFile);
      if (isRemedial) {
        setRemedialImages(prev => ({ ...prev, [subTaskId]: objectUrl }));
      } else {
        setImages(prev => ({ ...prev, [subTaskId]: objectUrl }));
      }
      setSubmittedTasks(prev => ({ ...prev, [subTaskId]: true }));
      if (onImageSelected) onImageSelected(task.id, subTaskId);

      const storageKey = `ob_progress_${task.id}`;
      const progress = JSON.parse(localStorage.getItem(storageKey) || '{}');
      progress[subTaskId] = {
        submitted: true,
        time: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(progress));

      // 3. Upload task definition
      const uploadTask = async () => {
        try {
          const dbInstance = await initDB();
          const record = await dbInstance.get('fotos', dbKey);
          const blob = record?.blob;
          if (!blob) throw new Error('Foto tidak ditemukan di IndexedDB');

          const formData = new FormData();
          formData.append('photo', blob, `ob_${task.id}_${subTaskId}.jpg`);
          formData.append('task_id', task.id);
          formData.append('subtask_index', subTaskId);
          formData.append('user_id', userData.id);

          const apiResponse = await fetch('https://jne-cilacap.com/slip/public/api/ob/report', {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
            },
            body: formData,
          });

          const result = await apiResponse.json();
          console.log('[OB Debug] Server response for subtask:', result);
        } catch (error) {
          console.error('[OB Debug] Sync error:', error);
        }
      };

      // Kirim ke server di background dengan queue
      if (apiQueue) {
        apiQueue.add(uploadTask);
      } else {
        uploadTask();
      }
    } catch (err) {
      console.error('Error submitting subtask:', err);
      Swal.fire('Gagal', err.message || 'Terjadi kesalahan.', 'error');
    }
  };

  const handleSubmit = async () => {
    const totalTasks = task.subTasks.length;

    // Gabungkan local submittedTasks + data dari serverReports agar akurat
    const doneCount = task.subTasks.filter((subTask) => {
      const subTaskId = subTask.id;
      // Sudah submit secara lokal?
      if (submittedTasks[subTaskId]) return true;
      // Sudah ada di server?
      const serverMatch = serverReports.find(
        r => parseInt(r.task_id) === parseInt(task.id) && parseInt(r.subtask_index) === parseInt(subTaskId)
      );
      return !!serverMatch;
    }).length;

    if (doneCount < totalTasks) {
      Swal.fire({
        icon: 'warning',
        title: 'Selesaikan Semua',
        text: `Masih ada ${totalTasks - doneCount} tugas yang belum dilaporkan.`,
        confirmButtonColor: '#2563eb'
      });
      return;
    }

    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          style={{
            backgroundColor: '#fff',
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1001,
            boxShadow: '0 -10px 40px rgba(0,0,0,0.1)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            padding: '24px 20px',
            borderBottom: '1px solid #f1f5f9',
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 2 }}>OFFICE BOY SYSTEM</div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1e293b' }}>{task.title}</h3>
            </div>
            <button onClick={onClose} style={{ padding: 8, background: '#f1f5f9', border: 'none', borderRadius: '50%', cursor: 'pointer' }}>
              <X size={20} color="#64748b" />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Form Input Tugas Tambahan Kustom (Hanya untuk TUGAS TAMBAHAN) */}
              {(task.id === 10 || task.id === 11) && (
                <div style={{
                  padding: '20px',
                  borderRadius: 24,
                  backgroundColor: '#f0f9ff',
                  border: '1px dashed #3b82f6',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.05)',
                  boxSizing: 'border-box'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase' }}>Tambah Tugas Baru</span>
                      <h4 style={{ margin: '4px 0 0 0', fontSize: 15, fontWeight: 700, color: '#1e3a8a', lineHeight: 1.4 }}>
                        Laporkan Tugas Tambahan Baru
                      </h4>
                      {(() => {
                        const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
                        const jabatan = (karyawan.jabatan || '').toLowerCase();
                        let penambah = null;
                        if (jabatan.includes('general affair')) penambah = 'Retno Adi Nugroho';
                        else if (jabatan.includes('human capital')) penambah = 'Kamalia Nur Fajriyah';
                        if (!penambah) return null;
                        return (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span>👤</span>
                            <span>Ditambahkan oleh: <strong style={{ color: '#334155' }}>{penambah}</strong></span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <input 
                      type="text"
                      value={newExtraTaskName}
                      onChange={(e) => setNewExtraTaskName(e.target.value)}
                      placeholder="Contoh: Bersihkan AC Ruang Meeting"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid #cbd5e1',
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#334155',
                        outline: 'none',
                        boxSizing: 'border-box',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
                        transition: 'border-color 0.2s'
                      }}
                    />
                  </div>

                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: 180,
                    borderRadius: 20,
                    overflow: 'hidden',
                    backgroundColor: '#f1f5f9',
                    marginBottom: 16
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      id="new-extra-photo"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setNewExtraTaskImage(URL.createObjectURL(file));
                          setNewExtraTaskFile(file);
                        }
                      }}
                    />
                    <label
                      htmlFor="new-extra-photo"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        backgroundImage: newExtraTaskImage ? `url(${newExtraTaskImage})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}
                    >
                      {!newExtraTaskImage && (
                        <>
                          <div style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          }}>
                            <Camera size={20} color="#3b82f6" />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Ambil Foto Bukti</span>
                        </>
                      )}
                    </label>
                  </div>

                  {newExtraTaskName && newExtraTaskImage && (
                    <button
                      onClick={handleCreateAndSubmitExtraTask}
                      disabled={isCreatingExtra}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 16,
                        border: 'none',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                        opacity: isCreatingExtra ? 0.7 : 1
                      }}
                    >
                      <Send size={16} /> 
                      {isCreatingExtra ? 'Sedang Mengirim...' : 'Kirim & Selesaikan Tugas'}
                    </button>
                  )}
                </div>
              )}

              {/* Garis pembatas jika ada form dan ada subtasks yang terkirim */}
              {(task.id === 10 || task.id === 11) && task.subTasks.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10,
                  margin: '8px 0'
                }}>
                  <div style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tugas Terkirim</span>
                  <div style={{ flex: 1, height: 1, backgroundColor: '#e2e8f0' }} />
                </div>
              )}

              {task.subTasks.map((subTask, idx) => {
                const subTaskId = subTask.id;

                // Cari data dari server reports dengan kombinasi task_id + subtask_index
                const serverReport = serverReports.find(
                  r => parseInt(r.task_id) === parseInt(task.id) && parseInt(r.subtask_index) === parseInt(subTaskId)
                );
                const isDikonfirmasi = serverReport?.status === 'Dikonfirmasi';
                const catatanAdmin = serverReport?.catatan;

                return (
                  <div key={subTaskId} style={{
                    padding: '20px',
                    borderRadius: 24,
                    backgroundColor: isDikonfirmasi ? '#eff6ff' : submittedTasks[subTaskId] ? '#f8fafc' : '#fff',
                    border: isDikonfirmasi ? '1.5px solid #3b82f6' : submittedTasks[subTaskId] ? '1px solid #e2e8f0' : '1px solid #f1f5f9',
                    boxShadow: isDikonfirmasi ? '0 4px 16px rgba(59,130,246,0.1)' : submittedTasks[subTaskId] ? 'none' : '0 4px 20px rgba(0,0,0,0.03)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>TUGAS {idx + 1}</span>
                        <h4 style={{ margin: '4px 0 0 0', fontSize: 15, fontWeight: 700, color: '#334155', lineHeight: 1.4 }}>{subTask.title}</h4>
                        {subTask.added_by_name && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <span>👤</span>
                            <span>Ditambahkan oleh: <strong style={{ color: '#334155' }}>{subTask.added_by_name}</strong></span>
                          </div>
                        )}
                      </div>
                      {/* Badge status */}
                      {isDikonfirmasi ? (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          backgroundColor: '#dbeafe', color: '#1d4ed8',
                          fontSize: 11, fontWeight: 700, padding: '4px 10px',
                          borderRadius: 20, flexShrink: 0, marginLeft: 8
                        }}>
                          <span>✓</span> Dikonfirmasi
                        </div>
                      ) : submittedTasks[subTaskId] ? (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          backgroundColor: '#dcfce7', color: '#16a34a',
                          fontSize: 11, fontWeight: 700, padding: '4px 10px',
                          borderRadius: 20, flexShrink: 0, marginLeft: 8
                        }}>
                          <span>✓</span> Terkirim
                        </div>
                      ) : null}
                    </div>

                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: 200,
                      borderRadius: 20,
                      overflow: 'hidden',
                      backgroundColor: '#f1f5f9'
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        id={`photo-${subTaskId}`}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (remedialMode[subTaskId]) {
                            handleFileSelect(subTaskId, file, true);
                          } else {
                            handleFileSelect(subTaskId, file, false);
                          }
                        }}
                        disabled={submittedTasks[subTaskId] && !remedialMode[subTaskId]}
                      />
                      <label
                        htmlFor={`photo-${subTaskId}`}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          cursor: (submittedTasks[subTaskId] && !remedialMode[subTaskId]) ? 'default' : 'pointer',
                          backgroundImage: remedialMode[subTaskId]
                            ? (remedialImages[subTaskId] ? `url(${remedialImages[subTaskId]})` : 'none')
                            : (images[subTaskId] ? `url(${images[subTaskId]})` : 'none'),
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        {!images[subTaskId] && (
                          <>
                            <div style={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              backgroundColor: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: 12,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                              <Camera size={24} color="#2563eb" />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#64748b' }}>Ambil Foto Bukti</span>
                          </>
                        )}
                        
                        {/* Prompt ambil foto di mode remidi */}
                        {remedialMode[subTaskId] && !remedialImages[subTaskId] && (
                          <>
                            <div style={{
                              width: 48, height: 48, borderRadius: '50%',
                              backgroundColor: '#fff', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              marginBottom: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                              <Camera size={24} color="#f59e0b" />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Ambil Foto Ulang</span>
                          </>
                        )}

                        {/* Overlay centang hijau saat sudah tersubmit (dan bukan mode remidi) */}
                        {submittedTasks[subTaskId] && !remedialMode[subTaskId] && (
                          <div style={{
                            position: 'absolute', inset: 0,
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <div style={{
                              backgroundColor: '#16a34a', borderRadius: '50%',
                              padding: 6, color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}>
                              <CheckCircle2 size={24} />
                            </div>
                          </div>
                        )}
                      </label>
                    </div>

                    {/* Catatan admin jika sudah dikonfirmasi */}
                    {isDikonfirmasi && catatanAdmin && (
                      <div style={{
                        marginTop: 12,
                        padding: '12px 14px',
                        backgroundColor: '#fffbeb',
                        borderRadius: 12,
                        borderLeft: '3px solid #f59e0b',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          📋 Catatan Admin — Perbaiki & Upload Ulang
                        </div>
                        <p style={{ margin: '0 0 10px 0', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                          {catatanAdmin}
                        </p>
                        {/* Tombol aktivasi mode remidi */}
                        {!remedialMode[subTaskId] && (
                          <button
                            onClick={() => {
                              setRemedialMode(prev => ({ ...prev, [subTaskId]: true }));
                              setRemedialImages(prev => ({ ...prev, [subTaskId]: null }));
                            }}
                            style={{
                              width: '100%', padding: '10px', borderRadius: 12,
                              border: '1.5px solid #f59e0b', backgroundColor: '#fef3c7',
                              color: '#92400e', fontSize: 13, fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', gap: 8
                            }}
                          >
                            <Camera size={15} /> Kerjakan Ulang
                          </button>
                        )}
                        {/* Tombol batal mode remidi */}
                        {remedialMode[subTaskId] && (
                          <button
                            onClick={() => {
                              setRemedialMode(prev => ({ ...prev, [subTaskId]: false }));
                              setRemedialImages(prev => ({ ...prev, [subTaskId]: null }));
                            }}
                            style={{
                              width: '100%', padding: '8px', borderRadius: 12,
                              border: '1px solid #cbd5e1', backgroundColor: '#f8fafc',
                              color: '#64748b', fontSize: 12, fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Batal
                          </button>
                        )}
                      </div>
                    )}

                    {/* Tombol submit normal (belum pernah submit) */}
                    {images[subTaskId] && !submittedTasks[subTaskId] && (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => handleSubmitSubTask(subTask)}
                        style={{
                        marginTop: 16,
                        width: '100%',
                        padding: '12px',
                        borderRadius: 16,
                        border: 'none',
                        backgroundColor: '#2563eb',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                    >
                      <Send size={16} /> Laporkan Tugas Ini
                    </motion.button>
                  )}

                    {/* Tombol submit ulang (mode remidi) */}
                    {remedialMode[subTaskId] && remedialImages[subTaskId] && (
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={() => {
                          handleSubmitSubTask(subTask, true);
                          setRemedialMode(prev => ({ ...prev, [subTaskId]: false }));
                          setRemedialImages(prev => ({ ...prev, [subTaskId]: null }));
                        }}
                        style={{
                          marginTop: 12, width: '100%', padding: '12px',
                          borderRadius: 16, border: 'none',
                          backgroundColor: '#f59e0b', color: '#fff',
                          fontSize: 14, fontWeight: 700, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          boxShadow: '0 4px 12px rgba(245,158,11,0.3)'
                        }}
                      >
                        <Send size={16} /> Kirim Foto Perbaikan
                      </motion.button>
                    )}
                  </div>
                );
              })}
          </div>
      </div>

          {/* Upload Log Panel */}
          <div style={{ borderTop: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
            <button
              onClick={() => {
                const next = !showUploadLog;
                setShowUploadLog(next);
                if (next) setUploadLogData(buildUploadLog());
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#475569',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClipboardList size={16} color="#64748b" />
                Upload Log
                {/* Indikator: berapa subtask belum ke server */}
                {(() => {
                  const log = buildUploadLog();
                  const pending = log.filter(l => l.localSaved && !l.serverStatus).length;
                  return pending > 0 ? (
                    <span style={{
                      backgroundColor: '#ef4444',
                      color: '#fff',
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 800,
                      padding: '1px 7px',
                      marginLeft: 2
                    }}>{pending} belum</span>
                  ) : null;
                })()}
              </span>
              {showUploadLog ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            <AnimatePresence>
              {showUploadLog && (
                <motion.div
                  key="upload-log"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ padding: '4px 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {uploadLogData.length === 0 && (
                      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>
                        Belum ada data lokal tersimpan.
                      </div>
                    )}
                    {uploadLogData.map((item) => {
                      const isPushing = pushingIds[item.subTaskId];
                      const isOnServer = !!item.serverStatus;
                      const hasLocal = item.localSaved;
                      return (
                        <div key={item.subTaskId} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '10px 14px',
                          borderRadius: 14,
                          backgroundColor: isOnServer ? '#f0fdf4' : hasLocal ? '#fffbeb' : '#f8fafc',
                          border: isOnServer ? '1px solid #bbf7d0' : hasLocal ? '1px solid #fde68a' : '1px solid #e2e8f0',
                        }}>
                          {/* Status icon */}
                          <div style={{ flexShrink: 0 }}>
                            {isOnServer ? (
                              <CheckCircle2 size={18} color="#16a34a" />
                            ) : hasLocal ? (
                              <AlertTriangle size={18} color="#f59e0b" />
                            ) : (
                              <Cloud size={18} color="#cbd5e1" />
                            )}
                          </div>

                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: '#334155',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>{item.title}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                              {isOnServer
                                ? `✓ Server — ${item.serverStatus}${item.serverAt ? ' · ' + new Date(item.serverAt).toLocaleString('id-ID', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}`
                                : hasLocal
                                  ? `⚠ Lokal saja${item.localTime ? ' · ' + new Date(item.localTime).toLocaleString('id-ID', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}`
                                  : 'Belum ada data'}
                            </div>
                          </div>

                          {/* Tombol Push (hanya jika ada lokal tapi belum ke server) */}
                          {hasLocal && !isOnServer && (
                            <motion.button
                              whileTap={{ scale: 0.93 }}
                              onClick={() => handlePushSubTask(item)}
                              disabled={isPushing}
                              style={{
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '6px 12px',
                                borderRadius: 10,
                                border: 'none',
                                backgroundColor: isPushing ? '#93c5fd' : '#2563eb',
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: isPushing ? 'not-allowed' : 'pointer',
                                boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
                                transition: 'background 0.2s'
                              }}
                            >
                              {isPushing ? (
                                <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
                              ) : (
                                <UploadCloud size={13} />
                              )}
                              {isPushing ? 'Mengirim...' : 'Push'}
                            </motion.button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div style={{ padding: '20px', borderTop: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: 16,
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Mengirim...' : 'Selesai & Tutup'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ObFormModal;
